var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import classNames from 'classnames';
import Picture from '../../components/Image/Picture';
import ImageLink from '../../components/Image/ImageLink';
import ResponsiveGrid from '../../components/ResponsiveGrid';
import { IMAGE } from '../../constants/cmsResourceTypes';
import { IMAGE_NAME } from '../../constants/analyticsNames';
import { BREAKPOINT_MAP } from './constants';
import styles from './index.module.scss';
const getSizes = (cqResponsive, fullWidth) => {
    var _a;
    const mappedCqResponsive = (_a = cqResponsive === null || cqResponsive === void 0 ? void 0 : cqResponsive.deviceBreakpoints) === null || _a === void 0 ? void 0 : _a.reduce((accumulator, { name, width, relativeWidth }) => {
        const breakpointWidth = Number(relativeWidth || width);
        if (name === 'default') {
            return Object.assign(Object.assign({}, accumulator), { default: fullWidth ? '100vw' : `${105 * breakpointWidth}px` });
        }
        return Object.assign(Object.assign({}, accumulator), { [name]: `${Math.floor((100 * breakpointWidth) / 12)}vw` });
    }, {});
    if (!mappedCqResponsive || !Object.entries(mappedCqResponsive).length)
        return '100vw';
    return Object.entries(BREAKPOINT_MAP)
        .map(([breakpoint, [, max]]) => {
        if (breakpoint === 'default')
            return ` ${mappedCqResponsive[breakpoint] || '100vw'}`;
        return `(max-width: ${max}px)  ${mappedCqResponsive[breakpoint] || '100vw'}`;
    })
        .join(', ');
};
const hasOverlayContent = (childComponents) => {
    var _a;
    const [responsiveGrid] = childComponents;
    return !!((_a = responsiveGrid === null || responsiveGrid === void 0 ? void 0 : responsiveGrid.childComponents) === null || _a === void 0 ? void 0 : _a.length);
};
const OVERLAY_POSITION = {
    top: 'flex-start',
    center: 'center',
    bottom: 'flex-end',
};
const Image = (_a) => {
    var _b, _c;
    var { cqResponsiveClasses = [], children = null, position = '', preloadImages = false } = _a, data = __rest(_a, ["cqResponsiveClasses", "children", "position", "preloadImages"]);
    const { altText = null, analytics = {}, analyticsTags = {}, assetMetadataHeight, assetMetadataWidth, childComponents = [], childComponentsVerticalAlignment = 'top', childComponentsVerticalAlignmentMobile, childComponentsVerticalAlignmentTablet, componentId = '', cqResponsive = {}, dynamicImages = {}, elementId = '', fileReference = '', fullWidth = false, height = '', linkURL = '', mobileImage = {}, tabletImage = {}, newWindow = false, overlayColor = '', presetQuality = 'highQuality95', } = data;
    const className = classNames(styles.wrapper, cqResponsiveClasses, 'waitrose-image', {
        [styles.fullWidth]: fullWidth,
    });
    const sizes = getSizes(cqResponsive, fullWidth);
    const imageLinkProps = {
        analytics,
        componentId,
        linkURL,
        position,
        newWindow,
    };
    return (React.createElement("section", Object.assign({ "data-testid": "image", className: className, id: elementId || componentId, "data-uniqueid": componentId, key: componentId }, analyticsTags),
        React.createElement("div", { "data-test": "color-wrapper", className: classNames(styles.colorWrapper, styles.fixedHeightWrapper) },
            React.createElement(ImageLink, Object.assign({}, imageLinkProps),
                React.createElement("div", { "data-testid": "fixed-height-images", style: {
                        '--default-fixed-height': height ? `${height}px` : null,
                        '--tablet-fixed-height': tabletImage.fileReference
                            ? (tabletImage.height && `${tabletImage.height}px`) || 'auto'
                            : null,
                        '--mobile-fixed-height': mobileImage.fileReference
                            ? (mobileImage.height && `${mobileImage.height}px`) || 'auto'
                            : null,
                    }, className: styles.fixedHeightImages },
                    React.createElement(Picture, { altText: altText, fileReference: fileReference, hasFixedHeight: !!height, height: height || assetMetadataHeight, scene7Url: dynamicImages === null || dynamicImages === void 0 ? void 0 : dynamicImages[presetQuality], scene7UrlMobile: (_b = mobileImage === null || mobileImage === void 0 ? void 0 : mobileImage.dynamicImages) === null || _b === void 0 ? void 0 : _b[presetQuality], mobileHeight: (mobileImage === null || mobileImage === void 0 ? void 0 : mobileImage.height) || (mobileImage === null || mobileImage === void 0 ? void 0 : mobileImage.assetMetadataHeight), mobileWidth: mobileImage === null || mobileImage === void 0 ? void 0 : mobileImage.assetMetadataWidth, scene7UrlTablet: (_c = tabletImage === null || tabletImage === void 0 ? void 0 : tabletImage.dynamicImages) === null || _c === void 0 ? void 0 : _c[presetQuality], tabletHeight: (tabletImage === null || tabletImage === void 0 ? void 0 : tabletImage.height) || (tabletImage === null || tabletImage === void 0 ? void 0 : tabletImage.assetMetadataHeight), tabletWidth: tabletImage === null || tabletImage === void 0 ? void 0 : tabletImage.assetMetadataWidth, sizes: sizes, width: assetMetadataWidth, preload: preloadImages })),
                (hasOverlayContent(childComponents) || children) && (React.createElement("div", { "data-testid": "component-over-image-wrapper", className: classNames(styles.commonOverlayStyles, styles.overlay, {
                        [styles.imageOverlayRelative]: false,
                        [styles.imageOverlayAbsolute]: true,
                    }), style: {
                        '--default-vertical-alignment': OVERLAY_POSITION[childComponentsVerticalAlignment],
                        '--tablet-vertical-alignment': OVERLAY_POSITION[childComponentsVerticalAlignmentTablet],
                        '--mobile-vertical-alignment': OVERLAY_POSITION[childComponentsVerticalAlignmentMobile],
                    } },
                    children,
                    React.createElement(ResponsiveGrid, { childComponents: childComponents, cqResponsive: cqResponsive, position: position }))),
                overlayColor && (React.createElement("div", { "data-testid": "color-layer", className: styles.colorLayer, style: { backgroundColor: overlayColor } }))))));
};
Image.displayName = IMAGE_NAME;
Image.resourceType = IMAGE;
export default Image;
//# sourceMappingURL=index.js.map